import React from 'react';
import classes from '../InviteUsersModal.module.scss';
import { Avatar, Button, Checkbox, Empty, Input, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import Assets from 'assets/icons';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hook';

type InvitingUsersProps = {
  emailIsValid: boolean;
  usersInput: string;
  onSearchUser: (event: React.ChangeEvent<HTMLInputElement>) => void
  addEmail: () => void;
  newEmails: string[];
  setNewEmails: React.Dispatch<React.SetStateAction<string[]>>;
  globalEmails: string[];
  setGlobalEmails: React.Dispatch<React.SetStateAction<string[]>>;
  currentError?: string;
};

const InvitingUsers = ({
  emailIsValid,
  usersInput,
  onSearchUser,
  addEmail,
  newEmails,
  setNewEmails,
  globalEmails,
  setGlobalEmails,
  currentError,
}: InvitingUsersProps) => {
  const { pendingInviteUsers, globalUsers, users } = useAppSelector((state) => state.dataUsers);
  
  const { t } = useTranslation();  

  const usersMailAlreadyInRoom = users.map(user => user.email);
  const filteredInviteUsers = React.useMemo(
    () =>
      pendingInviteUsers.filter(({ email }) => 
        email?.toLocaleLowerCase().includes(usersInput.toLocaleLowerCase())
      ),
    [pendingInviteUsers, usersInput]
  );

  const alreadyInvited = pendingInviteUsers.map(({email}) => email);

  const filteredGlobalUsers = React.useMemo(
    () =>
      globalUsers.filter(({ email, first_name, last_name }) => 
        (email?.toLocaleLowerCase().includes(usersInput.toLocaleLowerCase())
        || first_name?.toLocaleLowerCase().includes(usersInput.toLocaleLowerCase())
        || last_name?.toLocaleLowerCase().includes(usersInput.toLocaleLowerCase()))
        && !alreadyInvited.includes(email)
      ),
    [globalUsers, usersInput, alreadyInvited]
  );

  const arrWithAllUsers = [...filteredInviteUsers, ...filteredGlobalUsers];

  const itemCheck = (email: string) => {    
    setGlobalEmails(prev => prev.includes(email) ? prev.filter(el => el !== email) : [...prev, email]);
  };

  const displayError = usersInput.length > 4 && (
    !filteredGlobalUsers[0]
    || usersMailAlreadyInRoom.includes(usersInput?.toLocaleLowerCase())
  ) && currentError;

  const availableToAddEmail = !usersMailAlreadyInRoom.includes(usersInput?.toLocaleLowerCase()) && emailIsValid;
  
  return (
    <div className={classes.wrapUsers}>
      <Input
        suffix={emailIsValid 
          ? (availableToAddEmail &&<Tooltip title={t('Users.modals.configureRoles.addEmail')}>
            <CheckOutlined className={classes.checkIcon} onClick={addEmail}/>
          </Tooltip>)
          : arrWithAllUsers[0] && <Assets.SearchIcon className={classes.searchIcon} />
        }
        status={displayError ? 'error' : undefined}
        allowClear
        value={usersInput}
        onPressEnter={emailIsValid ? addEmail : () => {}}
        onBlur={emailIsValid ? addEmail : () => {}}
        onChange={onSearchUser}
        placeholder={t('Users.modals.configureRoles.searchUser')}
        className={classes.searchUser}
      />

      { displayError &&
        <div className={classes.error}>{currentError}</div>
      }

      {newEmails[0] && <div className={classes.allTags}>
        {newEmails.map(tag => (<div className={classes.tagWrap} key={tag}>
          <Avatar size={'small'} className={classes.avatar}>
            <span className={classes.avatarWord}>{tag[0]}</span>
          </Avatar>
          {tag}
          <CloseOutlined className={classes.close} onClick={() => setNewEmails(newEmails.filter((sometag) => sometag !== tag))}/>
        </div>))}
      </div>}

      <div className={classes.listOfUser} style={{ height: `calc(100% - 38px - ${displayError ? 26 : 0}px - ${newEmails[0] ? 32 : 0}px)`}}>
        {
          filteredInviteUsers && filteredInviteUsers[0] && (<>
            <div className={classes.contacts}>{t('Users.modals.configureRoles.invited')}</div>
            <div className={classes.users}>
              {filteredInviteUsers.map(user => {
                const currentUser = globalUsers.find(({email}) => email === user.email);                  
                return <div key={user.id} className={classes.userWrap}>
                <div className={classes.name}>
                  <Avatar size={'large'} className={classes.avatar}>
                    <span className={classes.avatarWord}>{
                      currentUser
                        ? currentUser.first_name[0] + currentUser.last_name[0]
                        : user.email[0]
                    }</span>
                  </Avatar>
                  
                  <div className={classes.text}>
                    <span>{currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : t('Users.modals.configureRoles.user') }</span>
                    <span className={classes.email}>{user.email}</span> 
                    </div>
                  </div>
                  
                  <Checkbox checked disabled/>
                </div>
              })}
            </div>
          </>)
        }

        {
          arrWithAllUsers?.length
          ? <div className={classes.users}>
            {filteredGlobalUsers[0] && <div className={classes.contacts}>{t('sidebar.contacts')}</div>}

            {filteredGlobalUsers.map(user => (
              <div key={user.id} className={classes.userWrap}>
                <div className={classes.name}>
                  <Avatar size={'large'} className={classes.avatar}>
                    <span className={classes.avatarWord}>{user.first_name[0] + user.last_name[0]}</span>
                  </Avatar>
                  
                  <div className={classes.text}>
                    <span>{`${user.first_name} ${user.last_name}`}</span>
                    <span className={classes.email}>{`${user.email}`}</span>
                  </div>
                </div>
                
                <Checkbox
                  checked={globalEmails.includes(user.email) || usersMailAlreadyInRoom.includes(user.email)}
                  disabled={usersMailAlreadyInRoom.includes(user.email)}
                  onClick={() => itemCheck(user.email)}
                />
                
              </div>
            ))}
          </div>
          : <div className={classes.emptyContacts}>
            <Empty
              className={classes.empty}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span>{t('Users.modals.configureRoles.emptyContacts')}</span>}
            />
            {emailIsValid && <Button type={'primary'} onClick={addEmail}>
              {t('Users.modals.configureRoles.addEmail')}
            </Button>}
          
          </div>
        }
      </div>
    </div>
  );
}

export default InvitingUsers;
